import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import HomePage from '../pages/HomePage.vue'
import OrderPage from '../pages/orders/OrderPage.vue'
import UnassignedOrderPage from '../pages/orders/UnassignedOrderPage.vue'
import SalesPage from '../pages/sales/SalesPage.vue'
import SalePage from '../pages/sales/SalePage.vue'
import DelayedShipments from '../pages/sales/DelayedShipments.vue'
import LateShipments from '../pages/sales/LateShipments.vue'
import InitiateReturnPage from '../pages/returns/InitiateReturnPage.vue'
import ImportPage from '@/pages/sales/ImportPage.vue'
import LoginPage from '../pages/LoginPage.vue'
import SignupPage from '../pages/SignupPage.vue'

const routes = [
  {
    path: '/',
    name: 'HomePage',
    component: HomePage
  },
  {
    path: '/login',
    name: 'LoginPage',
    component: LoginPage
  },
  {
    path: '/signup',
    name: 'SignupPage',
    component: SignupPage
  },
  {
    path: '/sales/all',
    name: 'SalesPage',
    component: SalesPage,
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/sales/:store',
    name: 'SalesPage',
    component: SalesPage,
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/sales/:saleId(\\d+)?',
    name: 'SalePage',
    component: SalePage,
    props: true
  },
  {
    path: '/order/other',
    name: 'UnassignedOrderPage',
    component: UnassignedOrderPage,
    props: true
  },
  {
    path: '/order/:vendor?',
    name: 'OrderPage',
    component: OrderPage,
    props: true
  },
  {
    path: '/sales/delayed-shipments',
    name: 'delayedShipments',
    component: DelayedShipments
  },
  {
    path: '/sales/late-shipments',
    name: 'lateShipments',
    component: LateShipments
  },
  {
    path: '/returns/initiate/:saleId(\\d+)',
    name: 'InitiateReturnPage',
    component: InitiateReturnPage,
    props: true
  },
  {
    path: '/returns/issue-rma/:returnId',
    name: 'IssueRMAPage',
    component: () => import('@/pages/returns/IssueRMAPage.vue'),
    props: true
  },
  {
    path: '/returns',
    name: 'returns',
    component: () => import('@/pages/returns/ReturnsPage.vue')
  },
  {
    path: '/returns/ebay',
    name: 'ebayReturns',
    component: () => import('@/pages/returns/ReturnsPage.vue')
  },
  {
    path: '/sales/import',
    name: 'sales-import',
    component: ImportPage,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/sales/export',
    name: 'sales-export',
    component: () => import('@/pages/sales/ExportPage.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/manage/credits',
    name: 'credits',
    component: () => import('@/pages/manage/CreditsPage.vue'),
    meta: {
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/manage/expenses',
    name: 'expenses',
    component: () => import('@/pages/manage/ExpensesPage.vue'),
    meta: {
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/manage/ebay-accounts',
    name: 'ebay-accounts',
    component: () => import('@/pages/manage/EbayAccountsPage.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/manage/reprice-formulas',
    name: 'reprice-formulas',
    component: () => import('@/pages/manage/RepeiceFormulasPage.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/reports/daily-profits',
    name: 'daily-profits',
    component: () => import('@/pages/reports/DailyProfitsPage.vue'),
    meta: {
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/reports/daily-average',
    name: 'daily-average',
    component: () => import('@/pages/reports/DailyAveragePage.vue'),
    meta: {
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/reports/monthly-profits',
    name: 'monthly-profits',
    component: () => import('@/pages/reports/MonthlyProfitsPage.vue'),
    meta: {
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/reports/yearly-profits',
    name: 'yearly-profits',
    component: () => import('@/pages/reports/YearlyProfitsPage.vue'),
    meta: {
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/inventory/stock-items',
    name: 'stock-items',
    component: () => import('@/pages/inventory/StockItemsPage.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/inventory/amazon-stores',
    name: 'amazon-stores',
    component: () => import('@/pages/inventory/AmazonStoresPage.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/inventory/ebay-stores',
    name: 'ebay-stores',
    component: () => import('@/pages/inventory/EbayStoresPage.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/inventory/walmart-stores',
    name: 'walmart-stores',
    component: () => import('@/pages/inventory/WalmartStoresPage.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/inventory/import-cost-shipping',
    name: 'import-cost-shipping',
    component: () => import('@/pages/inventory/ImportCostShippingPage.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/export-status/:id',
    name: 'export-status',
    component: () => import('@/pages/ExporStatustPage.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/import-status/:id',
    name: 'import-status',
    component: () => import('@/pages/ImportStatustPage.vue'),
    meta: {
      requiresAuth: true
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  
  if (to.matched.some(record => record.name !== 'LoginPage' && record.name !== 'SignupPage')) {
    if (!authStore.isAuthenticated) {
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    } else {
      next()
    }
  } else if (to.matched.some(record => record.meta.adminOnly) && !authStore.isAdmin) {
    next({
      path: '/',
    })
  } else {
    next()
  }
})

export default router 